import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Checkbox, Dialog, DialogContent, Divider, FilledInput, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Stack, Tab, Tabs, TextField, Typography} from '@mui/material';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {makeStyles, styled} from '@mui/styles';
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {Sessions} from './sessions';
import OtpInput from 'react-otp-input';
import {useForm} from 'react-hook-form';
import {useAuth} from '../AuthProvider/AuthProvider';
import {fetchAdminUserSettings, fetchCustomerUserSettings, saveAdminUserSettings, saveCustomerUserSettings, createOtp, createPassword} from "../../utils/apiCalls";

import {StyledButtonContained, StyledButtonOutlined} from "../../utils/uiUtils";


const settingsTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                outlinedPrimary: {
                    border: '1px solid #FFC540',
                    color: '#FFC540',
                    '&:hover': {
                        border: '1px solid #FFC540',
                        color: '#FFC540',
                    },
                },
                outlinedSecondary: {
                    border: '1px solid #FFFFFF',
                    color: '#FFFFFF',
                    '&:hover': {
                        border: '1px solid #FFFFFF',
                        color: '#FFFFFF',
                    },
                },
                outlinedSizeSmall: {
                    width: '90px',
                    height: '40px',
                    borderRadius: '30px',
                },
                outlinedSizeMedium: {
                    width: '220px',
                    height: '60px',
                    borderRadius: '30px',
                },
                root: {
                    textTransform: 'none',

                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    width: '450px',
                    height: '60px',
                    backgroundColor: 'rgba(217,217,217,0.1)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    '&.Mui-focused': {
                        backgroundColor: 'rgba(217,217,217,0.1)',
                        width: '450px',
                        height: '60px',
                        borderRadius: '8px',
                    },
                },
                input: {
                    //fontFamily: 'Poppins', 
                    fontWeight: 400,
                    lineHeight: '25px',
                    fontSize: 18,
                    letterSpacing: '0em',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#C7CCD0',
                    '&.Mui-focused': {
                        color: '#FFC540'
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    height: '100vh',
                    borderRight: 'none',
                    borderRadius: '21px, 0px, 0px, 21px',
                    textTransform: 'none',
                    '& .css-ra670y-MuiButtonBase-root-MuiTab-root': {
                        maxWidth: '100%',
                    },
                },
                vertical: {
                    background: 'rgba(217,217,217,0.1)',
                    color: '#FFFFFF',
                    borderRight: 'none',
                },
                flexContainer: {
                    paddingTop: '50px',
                    marginLeft: '30px',
                },
                scroller: {},
                indicator: {
                    background: '#3A3E3F',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    alignItems: 'flex-start',
                    textTransform: 'none',
                    height: '40px',
                    color: '#FFFFFF',
                    '&.Mui-selected': {
                        color: '#FFC540',
                        borderRadius: '21px 0px 0px 21px',
                        height: '40px',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    width: '25%',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderRadius: '8px',
                            borderColor: '#C7CCD0',
                        },
                        '&:hover fieldset': {
                            borderColor: '#C7CCD0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#C7CCD0',
                        }
                    },
                    '& .MuiOutlinedInput-input': {
                        color: '#FFFFFF',
                    },
                    '& svg': {
                        color: '#FFFFFF'
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: 'red',
                    },
                }
            }

        }
    },
});


const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'stretch',
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '28px',
        fontWeight: 700,
        lineHeight: '34px',
        letterSpacing: '0em',
        textAlign: 'left',
        color: '#FFFFFF',
        padding: 2,
    },
    divider: {
        border: '1px solid #EAEAEA',
        width: '100%'
    },
    tabs: {
        background: '#FFFFFF1A',
        color: '#FFFFFF',
        borderRight: 'none',
        alignContent: 'left'
    },
    activeTab: {
        background: '#3A3E3F !important',
        color: '#FFC540 !important',
    },
    tabPanel: {
        background: '#3A3E3F',
        color: '#FFFFFF',
        padding: 2,
    },
}));

function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{p: 3}}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const CustomTabList = styled(Tabs)(() => ({
    borderRight: 0,
    maxWidth: '100% !important',
    '& .MuiTabs-flexContainer': {
        width: '100%',
    },
    '& .MuiTabs-scroller': {
        background: 'rgba(255,255,255,0.1)',
    },
    '& .MuiTabs-indicator': {
        background: '#3A3E3F',
    },
    '& .MuiTab-root': {
        color: '#FFFFFF',
        borderRadius: '21px, 0px, 0px, 21px',
    },
    textTransform: 'none',
}));

const CustomTab = styled(Tab)((activeTab) => ({
    mt: 4,
    background: (activeTab === 0) ? '#3A3E3F' : 'transparent',
    textTransform: 'none',
    '&.Mui-selected': {
        color: '#FFC540',
        borderRadius: '21px 0px 0px 21px',
        height: '40px',
    }
}));

export const Settings = ({loggedInUser = 'Test User'}) => {// will have to see where it gets this logged in user
    const {user: authUser, isAdmin, logout} = useAuth();
    const userId = authUser?.uuid;
    const customerId = authUser?.customer_uuid;
    const userEmail = authUser?.email;
    const classes = useStyles();
    const user = authUser;
    const [activeTab, setActiveTab] = useState(0);
    //General
    const [autoTimeZone, setAutoTimeZone] = useState(false);
    const [selectedTimeZone, setSelectedTimeZone] = useState('');
    const [notifyProducts, setNotifyProducts] = useState("");
    const [notifyScenarios, setNotifyScenarios] = useState("");
    const [emailNotifications, setEmailNotifications] = useState("");
    const [pushNotifications, setPushNotifications] = useState("");
    // const timeZones = ["Africa/Abidjan", "Africa/Accra", "Africa/Addis_Ababa", "Africa/Algiers", "Africa/Asmara", "Africa/Asmera", "Africa/Bamako", "Africa/Bangui", "Africa/Banjul", "Africa/Bissau", "Africa/Blantyre", "Africa/Brazzaville", "Africa/Bujumbura", "Africa/Cairo", "Africa/Casablanca", "Africa/Ceuta", "Africa/Conakry", "Africa/Dakar", "Africa/Dar_es_Salaam", "Africa/Djibouti", "Africa/Douala", "Africa/El_Aaiun", "Africa/Freetown", "Africa/Gaborone", "Africa/Harare", "Africa/Johannesburg", "Africa/Juba", "Africa/Kampala", "Africa/Khartoum", "Africa/Kigali", "Africa/Kinshasa", "Africa/Lagos", "Africa/Libreville", "Africa/Lome", "Africa/Luanda", "Africa/Lubumbashi", "Africa/Lusaka", "Africa/Malabo", "Africa/Maputo", "Africa/Maseru", "Africa/Mbabane", "Africa/Mogadishu", "Africa/Monrovia", "Africa/Nairobi", "Africa/Ndjamena", "Africa/Niamey", "Africa/Nouakchott", "Africa/Ouagadougou", "Africa/Porto-Novo", "Africa/Sao_Tome", "Africa/Timbuktu", "Africa/Tripoli", "Africa/Tunis", "Africa/Windhoek", "America/Adak", "America/Anchorage", "America/Anguilla", "America/Antigua", "America/Araguaina", "America/Argentina/Buenos_Aires", "America/Argentina/Catamarca", "America/Argentina/ComodRivadavia", "America/Argentina/Cordoba", "America/Argentina/Jujuy", "America/Argentina/La_Rioja", "America/Argentina/Mendoza", "America/Argentina/Rio_Gallegos", "America/Argentina/Salta", "America/Argentina/San_Juan", "America/Argentina/San_Luis", "America/Argentina/Tucuman", "America/Argentina/Ushuaia", "America/Aruba", "America/Asuncion", "America/Atikokan", "America/Atka", "America/Bahia", "America/Bahia_Banderas", "America/Barbados", "America/Belem", "America/Belize", "America/Blanc-Sablon", "America/Boa_Vista", "America/Bogota", "America/Boise", "America/Buenos_Aires", "America/Cambridge_Bay", "America/Campo_Grande", "America/Cancun", "America/Caracas", "America/Catamarca", "America/Cayenne", "America/Cayman", "America/Chicago", "America/Chihuahua", "America/Ciudad_Juarez", "America/Coral_Harbour", "America/Cordoba", "America/Costa_Rica", "America/Creston", "America/Cuiaba", "America/Curacao", "America/Danmarkshavn", "America/Dawson", "America/Dawson_Creek", "America/Denver", "America/Detroit", "America/Dominica", "America/Edmonton", "America/Eirunepe", "America/El_Salvador", "America/Ensenada", "America/Fort_Nelson", "America/Fort_Wayne", "America/Fortaleza", "America/Glace_Bay", "America/Godthab", "America/Goose_Bay", "America/Grand_Turk", "America/Grenada", "America/Guadeloupe", "America/Guatemala", "America/Guayaquil", "America/Guyana", "America/Halifax", "America/Havana", "America/Hermosillo", "America/Indiana/Indianapolis", "America/Indiana/Knox", "America/Indiana/Marengo", "America/Indiana/Petersburg", "America/Indiana/Tell_City", "America/Indiana/Vevay", "America/Indiana/Vincennes", "America/Indiana/Winamac", "America/Indianapolis", "America/Inuvik", "America/Iqaluit", "America/Jamaica", "America/Jujuy", "America/Juneau", "America/Kentucky/Louisville", "America/Kentucky/Monticello", "America/Knox_IN", "America/Kralendijk", "America/La_Paz", "America/Lima", "America/Los_Angeles", "America/Louisville", "America/Lower_Princes", "America/Maceio", "America/Managua", "America/Manaus", "America/Marigot", "America/Martinique", "America/Matamoros", "America/Mazatlan", "America/Mendoza", "America/Menominee", "America/Merida", "America/Metlakatla", "America/Mexico_City", "America/Miquelon", "America/Moncton", "America/Monterrey", "America/Montevideo", "America/Montreal", "America/Montserrat", "America/Nassau", "America/New_York", "America/Nipigon", "America/Nome", "America/Noronha", "America/North_Dakota/Beulah", "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "America/Nuuk", "America/Ojinaga", "America/Panama", "America/Pangnirtung", "America/Paramaribo", "America/Phoenix", "America/Port-au-Prince", "America/Port_of_Spain", "America/Porto_Acre", "America/Porto_Velho", "America/Puerto_Rico", "America/Punta_Arenas", "America/Rainy_River", "America/Rankin_Inlet", "America/Recife", "America/Regina", "America/Resolute", "America/Rio_Branco", "America/Rosario", "America/Santa_Isabel", "America/Santarem", "America/Santiago", "America/Santo_Domingo", "America/Sao_Paulo", "America/Scoresbysund", "America/Shiprock", "America/Sitka", "America/St_Barthelemy", "America/St_Johns", "America/St_Kitts", "America/St_Lucia", "America/St_Thomas", "America/St_Vincent", "America/Swift_Current", "America/Tegucigalpa", "America/Thule", "America/Thunder_Bay", "America/Tijuana", "America/Toronto", "America/Tortola", "America/Vancouver", "America/Virgin", "America/Whitehorse", "America/Winnipeg", "America/Yakutat", "America/Yellowknife", "Antarctica/Casey", "Antarctica/Davis", "Antarctica/DumontDUrville", "Antarctica/Macquarie", "Antarctica/Mawson", "Antarctica/McMurdo", "Antarctica/Palmer", "Antarctica/Rothera", "Antarctica/South_Pole", "Antarctica/Syowa", "Antarctica/Troll", "Antarctica/Vostok", "Arctic/Longyearbyen", "Asia/Aden", "Asia/Almaty", "Asia/Amman", "Asia/Anadyr", "Asia/Aqtau", "Asia/Aqtobe", "Asia/Ashgabat", "Asia/Ashkhabad", "Asia/Atyrau", "Asia/Baghdad", "Asia/Bahrain", "Asia/Baku", "Asia/Bangkok", "Asia/Barnaul", "Asia/Beirut", "Asia/Bishkek", "Asia/Brunei", "Asia/Calcutta", "Asia/Chita", "Asia/Choibalsan", "Asia/Chongqing", "Asia/Chungking", "Asia/Colombo", "Asia/Dacca", "Asia/Damascus", "Asia/Dhaka", "Asia/Dili", "Asia/Dubai", "Asia/Dushanbe", "Asia/Famagusta", "Asia/Gaza", "Asia/Harbin", "Asia/Hebron", "Asia/Ho_Chi_Minh", "Asia/Hong_Kong", "Asia/Hovd", "Asia/Irkutsk", "Asia/Istanbul", "Asia/Jakarta", "Asia/Jayapura", "Asia/Jerusalem", "Asia/Kabul", "Asia/Kamchatka", "Asia/Karachi", "Asia/Kashgar", "Asia/Kathmandu", "Asia/Katmandu", "Asia/Khandyga", "Asia/Kolkata", "Asia/Krasnoyarsk", "Asia/Kuala_Lumpur", "Asia/Kuching", "Asia/Kuwait", "Asia/Macao", "Asia/Macau", "Asia/Magadan", "Asia/Makassar", "Asia/Manila", "Asia/Muscat", "Asia/Nicosia", "Asia/Novokuznetsk", "Asia/Novosibirsk", "Asia/Omsk", "Asia/Oral", "Asia/Phnom_Penh", "Asia/Pontianak", "Asia/Pyongyang", "Asia/Qatar", "Asia/Qostanay", "Asia/Qyzylorda", "Asia/Rangoon", "Asia/Riyadh", "Asia/Saigon", "Asia/Sakhalin", "Asia/Samarkand", "Asia/Seoul", "Asia/Shanghai", "Asia/Singapore", "Asia/Srednekolymsk", "Asia/Taipei", "Asia/Tashkent", "Asia/Tbilisi", "Asia/Tehran", "Asia/Tel_Aviv", "Asia/Thimbu", "Asia/Thimphu", "Asia/Tokyo", "Asia/Tomsk", "Asia/Ujung_Pandang", "Asia/Ulaanbaatar", "Asia/Ulan_Bator", "Asia/Urumqi", "Asia/Ust-Nera", "Asia/Vientiane", "Asia/Vladivostok", "Asia/Yakutsk", "Asia/Yangon", "Asia/Yekaterinburg", "Asia/Yerevan", "Atlantic/Azores", "Atlantic/Bermuda", "Atlantic/Canary", "Atlantic/Cape_Verde", "Atlantic/Faeroe", "Atlantic/Faroe", "Atlantic/Jan_Mayen", "Atlantic/Madeira", "Atlantic/Reykjavik", "Atlantic/South_Georgia", "Atlantic/St_Helena", "Atlantic/Stanley", "Australia/ACT", "Australia/Adelaide", "Australia/Brisbane", "Australia/Broken_Hill", "Australia/Canberra", "Australia/Currie", "Australia/Darwin", "Australia/Eucla", "Australia/Hobart", "Australia/LHI", "Australia/Lindeman", "Australia/Lord_Howe", "Australia/Melbourne", "Australia/NSW", "Australia/North", "Australia/Perth", "Australia/Queensland", "Australia/South", "Australia/Sydney", "Australia/Tasmania", "Australia/Victoria", "Australia/West", "Australia/Yancowinna", "Brazil/Acre", "Brazil/DeNoronha", "Brazil/East", "Brazil/West", "CET", "CST6CDT", "Canada/Atlantic", "Canada/Central", "Canada/Eastern", "Canada/Mountain", "Canada/Newfoundland", "Canada/Pacific", "Canada/Saskatchewan", "Canada/Yukon", "Chile/Continental", "Chile/EasterIsland", "Cuba", "EET", "EST", "EST5EDT", "Egypt", "Eire", "Etc/GMT", "Etc/GMT+0", "Etc/GMT+1", "Etc/GMT+10", "Etc/GMT+11", "Etc/GMT+12", "Etc/GMT+2", "Etc/GMT+3", "Etc/GMT+4", "Etc/GMT+5", "Etc/GMT+6", "Etc/GMT+7", "Etc/GMT+8", "Etc/GMT+9", "Etc/GMT-0", "Etc/GMT-1", "Etc/GMT-10", "Etc/GMT-11", "Etc/GMT-12", "Etc/GMT-13", "Etc/GMT-14", "Etc/GMT-2", "Etc/GMT-3", "Etc/GMT-4", "Etc/GMT-5", "Etc/GMT-6", "Etc/GMT-7", "Etc/GMT-8", "Etc/GMT-9", "Etc/GMT0", "Etc/Greenwich", "Etc/UCT", "Etc/UTC", "Etc/Universal", "Etc/Zulu", "Europe/Amsterdam", "Europe/Andorra", "Europe/Astrakhan", "Europe/Athens", "Europe/Belfast", "Europe/Belgrade", "Europe/Berlin", "Europe/Bratislava", "Europe/Brussels", "Europe/Bucharest", "Europe/Budapest", "Europe/Busingen", "Europe/Chisinau", "Europe/Copenhagen", "Europe/Dublin", "Europe/Gibraltar", "Europe/Guernsey", "Europe/Helsinki", "Europe/Isle_of_Man", "Europe/Istanbul", "Europe/Jersey", "Europe/Kaliningrad", "Europe/Kiev", "Europe/Kirov", "Europe/Kyiv", "Europe/Lisbon", "Europe/Ljubljana", "Europe/London", "Europe/Luxembourg", "Europe/Madrid", "Europe/Malta", "Europe/Mariehamn", "Europe/Minsk", "Europe/Monaco", "Europe/Moscow", "Europe/Nicosia", "Europe/Oslo", "Europe/Paris", "Europe/Podgorica", "Europe/Prague", "Europe/Riga", "Europe/Rome", "Europe/Samara", "Europe/San_Marino", "Europe/Sarajevo", "Europe/Saratov", "Europe/Simferopol", "Europe/Skopje", "Europe/Sofia", "Europe/Stockholm", "Europe/Tallinn", "Europe/Tirane", "Europe/Tiraspol", "Europe/Ulyanovsk", "Europe/Uzhgorod", "Europe/Vaduz", "Europe/Vatican", "Europe/Vienna", "Europe/Vilnius", "Europe/Volgograd", "Europe/Warsaw", "Europe/Zagreb", "Europe/Zaporozhye", "Europe/Zurich", "GB", "GB-Eire", "GMT", "GMT+0", "GMT-0", "GMT0", "Greenwich", "HST", "Hongkong", "Iceland", "Indian/Antananarivo", "Indian/Chagos", "Indian/Christmas", "Indian/Cocos", "Indian/Comoro", "Indian/Kerguelen", "Indian/Mahe", "Indian/Maldives", "Indian/Mauritius", "Indian/Mayotte", "Indian/Reunion", "Iran", "Israel", "Jamaica", "Japan", "Kwajalein", "Libya", "MET", "MST", "MST7MDT", "Mexico/BajaNorte", "Mexico/BajaSur", "Mexico/General", "NZ", "NZ-CHAT", "Navajo", "PRC", "PST8PDT", "Pacific/Apia", "Pacific/Auckland", "Pacific/Bougainville", "Pacific/Chatham", "Pacific/Chuuk", "Pacific/Easter", "Pacific/Efate", "Pacific/Enderbury", "Pacific/Fakaofo", "Pacific/Fiji", "Pacific/Funafuti", "Pacific/Galapagos", "Pacific/Gambier", "Pacific/Guadalcanal", "Pacific/Guam", "Pacific/Honolulu", "Pacific/Johnston", "Pacific/Kanton", "Pacific/Kiritimati", "Pacific/Kosrae", "Pacific/Kwajalein", "Pacific/Majuro", "Pacific/Marquesas", "Pacific/Midway", "Pacific/Nauru", "Pacific/Niue", "Pacific/Norfolk", "Pacific/Noumea", "Pacific/Pago_Pago", "Pacific/Palau", "Pacific/Pitcairn", "Pacific/Pohnpei", "Pacific/Ponape", "Pacific/Port_Moresby", "Pacific/Rarotonga", "Pacific/Saipan", "Pacific/Samoa", "Pacific/Tahiti", "Pacific/Tarawa", "Pacific/Tongatapu", "Pacific/Truk", "Pacific/Wake", "Pacific/Wallis", "Pacific/Yap", "Poland", "Portugal", "ROC", "ROK", "Singapore", "Turkey", "UCT", "US/Alaska", "US/Aleutian", "US/Arizona", "US/Central", "US/East-Indiana", "US/Eastern", "US/Hawaii", "US/Indiana-Starke", "US/Michigan", "US/Mountain", "US/Pacific", "US/Samoa", "UTC", "Universal", "W-SU", "WET", "Zulu"]
    const timeZones = [
        {
            "id": "baker_island_time",
            "value": "(UTC-12:00) Baker Island Time"
        },
        {
            "id": "niue_time",
            "value": "(UTC-11:00) Niue Time"
        },
        {
            "id": "hawaii_aleutian_standard_time",
            "value": "(UTC-10:00) Hawaii-Aleutian Standard Time"
        },
        {
            "id": "alaska_standard_time",
            "value": "(UTC-09:00) Alaska Standard Time"
        },
        {
            "id": "pacific_standard_time",
            "value": "(UTC-08:00) Pacific Standard Time"
        },
        {
            "id": "mountain_standard_time",
            "value": "(UTC-07:00) Mountain Standard Time"
        },
        {
            "id": "central_standard_time",
            "value": "(UTC-06:00) Central Standard Time"
        },
        {
            "id": "eastern_standard_time",
            "value": "(UTC-05:00) Eastern Standard Time"
        },
        {
            "id": "atlantic_standard_time",
            "value": "(UTC-04:00) Atlantic Standard Time"
        },
        {
            "id": "newfoundland_standard_time",
            "value": "(UTC-03:30) Newfoundland Standard Time"
        },
        {
            "id": "argentina_standard_time",
            "value": "(UTC-03:00) Argentina Standard Time"
        },
        {
            "id": "south_georgia_time",
            "value": "(UTC-02:00) South Georgia Time"
        },
        {
            "id": "cape_verde_time",
            "value": "(UTC-01:00) Cape Verde Time"
        },
        {
            "id": "coordinated_universal_time",
            "value": "(UTC) Coordinated Universal Time"
        },
        {
            "id": "central_european_time",
            "value": "(UTC+01:00) Central European Time"
        },
        {
            "id": "eastern_european_time",
            "value": "(UTC+02:00) Eastern European Time"
        },
        {
            "id": "moscow_standard_time",
            "value": "(UTC+03:00) Moscow Standard Time"
        },
        {
            "id": "iran_standard_time",
            "value": "(UTC+03:30) Iran Standard Time"
        },
        {
            "id": "gulf_standard_time",
            "value": "(UTC+04:00) Gulf Standard Time"
        },
        {
            "id": "afghanistan_time",
            "value": "(UTC+04:30) Afghanistan Time"
        },
        {
            "id": "pakistan_standard_time",
            "value": "(UTC+05:00) Pakistan Standard Time"
        },
        {
            "id": "india_standard_time",
            "value": "(UTC+05:30) India Standard Time"
        },
        {
            "id": "nepal_time",
            "value": "(UTC+05:45) Nepal Time"
        },
        {
            "id": "bangladesh_standard_time",
            "value": "(UTC+06:00) Bangladesh Standard Time"
        },
        {
            "id": "myanmar_time",
            "value": "(UTC+06:30) Myanmar Time"
        },
        {
            "id": "indochina_time",
            "value": "(UTC+07:00) Indochina Time"
        },
        {
            "id": "china_standard_time",
            "value": "(UTC+08:00) China Standard Time"
        },
        {
            "id": "singapore_standard_time",
            "value": "(UTC+08:00) Singapore Standard Time"
        },
        {
            "id": "australian_western_standard_time",
            "value": "(UTC+08:00) Australian Western Standard Time"
        },
        {
            "id": "southeastern_western_australia_standard_time",
            "value": "(UTC+08:45) Southeastern Western Australia Standard Time"
        },
        {
            "id": "japan_standard_time",
            "value": "(UTC+09:00) Japan Standard Time"
        },
        {
            "id": "korea_standard_time",
            "value": "(UTC+09:00) Korea Standard Time"
        },
        {
            "id": "australian_central_standard_time",
            "value": "(UTC+09:30) Australian Central Standard Time"
        },
        {
            "id": "australian_eastern_standard_time",
            "value": "(UTC+10:00) Australian Eastern Standard Time"
        },
        {
            "id": "lord_howe_standard_time",
            "value": "(UTC+10:30) Lord Howe Standard Time"
        },
        {
            "id": "solomon_islands_time",
            "value": "(UTC+11:00) Solomon Islands Time"
        },
        {
            "id": "norfolk_island_time",
            "value": "(UTC+11:30) Norfolk Island Time"
        },
        {
            "id": "new_zealand_standard_time",
            "value": "(UTC+12:00) New Zealand Standard Time"
        },
        {
            "id": "chatham_standard_time",
            "value": "(UTC+12:45) Chatham Standard Time"
        },
        {
            "id": "tonga_time",
            "value": "(UTC+13:00) Tonga Time"
        },
        {
            "id": "line_islands_time",
            "value": "(UTC+14:00) Line Islands Time"
        }
    ]
    // Account
    const [password, setPassword] = useState(1234578687);
    const [showPassword, setShowPassword] = useState(false);
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);
    const [otpDialog, setOtpDialog] = useState(false);
    const [otp, setOtp] = useState('');
    const [changeDailog, setChangeDialog] = useState(false);
    const {
        register, handleSubmit, getValues, watch, formState: {
            errors
        }
    } = useForm();
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowPassword1 = () => setShowPassword1((show) => !show);
    const handleClickShowPassword2 = () => setShowPassword2((show) => !show);
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };
    const handleMouseDownPassword1 = (event) => {
        event.preventDefault();
    };
    const handleMouseDownPassword2 = (event) => {
        event.preventDefault();
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };
    const screenHeight = window.innerHeight;
    useEffect(() => {
        userDetails();
    }, []);
    const handleLogout = () => {
        logout();
    }
    const handlechangeDeactivate = () => {
        saveCustomerUserSettings(customerId, userId, {is_active: false})
            .then(() => {
                handleLogout();
            }).catch((e) => {
                console.log(e)
            })
    }
    const capitalizeString = (_string) => {
        if (typeof _string != "string") {
            _string = _string.toLocaleString();
        }
        return _string[0].toUpperCase() + _string.slice(1);
    }
    const userDetails = () => {
        if (isAdmin) {
            fetchAdminUserSettings(userId).then(data => {
                if (data) {
                    setAutoTimeZone(capitalizeString(data?.automatic_timezone));
                    setNotifyProducts(capitalizeString(data?.email_new_products?.toLocaleString()));
                    setNotifyScenarios(capitalizeString(data?.email_offers_updates?.toLocaleString()));
                    setEmailNotifications(capitalizeString(data?.email_notifications?.toLocaleString()));
                    setPushNotifications(capitalizeString(data?.push_notifications?.toLocaleString()));
                    data?.timezone && setSelectedTimeZone(capitalizeString(data?.timezone?.toLocaleString()));
                    update2StepDetails(data);
                }
            }).catch(err => {
                console.log(err);
            })
        } else {
            fetchCustomerUserSettings(customerId, userEmail).then(data => {
                if (data) {
                    setAutoTimeZone(data.automatic_timezone);
                    setNotifyProducts(data.email_new_products);
                    setNotifyScenarios(data.email_offers_updates);
                    setEmailNotifications(data.email_notifications);
                    setPushNotifications(data.push_notifications);
                    setSelectedTimeZone(data.timezone);
                    update2StepDetails(data);
                }
            }).catch(err => {
                console.log(err);
            })
        }
    }
    const update2StepDetails = (data) => {
        if (data.two_step_verification) {
            if (data.two_step_verification == 'sms') {
                // setValue("verify_by_sms", true);
                setTwoFAMode('verify_by_sms');
            } else if (data.two_step_verification == 'authenticator') {
                // setValue("verify_by_authenticator", true);
                setTwoFAMode('verify_by_authenticator');
            } else {
                // setValue("verify_by_email", true)
                setTwoFAMode('verify_by_email');
            }
            // setValue("verify_by_sms", response.data.mfa_sms);
            // setValue("verify_by_email", response.data.mfa_email);
            // setValue("verify_by_authenticator", response.data.mfa_authenticator);
        }
    }
    //handling patch req of automatic timeZone selection
    const handletimeZoneAutomatic = (e) => {
        let isChecked = JSON.parse(JSON.stringify(e.target.checked));
        setAutoTimeZone(capitalizeString(e.target.checked));
        const payload = {automatic_timezone: isChecked};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    //handling patch req of new products
    const handlenotifyProducts = () => {
        setNotifyProducts(notifyProducts === 'True' ? 'False' : 'True')
        const payload = {email_new_products: notifyProducts === 'True' ? false : true};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    //handling patch req of E-mail offers & Updates
    const handlenotifyScenarios = () => {
        setNotifyScenarios(notifyScenarios === 'True' ? 'False' : 'True')
        const payload = {email_offers_updates: notifyScenarios === 'True' ? false : true};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    //handling patch req of email Notification
    const handleemailNotifications = () => {
        setEmailNotifications(emailNotifications === 'True' ? 'False' : 'True')
        const payload = {email_notifications: emailNotifications === 'True' ? false : true};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    //handling patch req of push Notification
    const handlePushNotifications = () => {
        setPushNotifications(pushNotifications === 'True' ? 'False' : 'True')
        const payload = {push_notifications: pushNotifications === 'True' ? false : true};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    const handleTimeZone = (e) => {
        setSelectedTimeZone(e.target.value);
        const payload = {timezone: e.target.value};
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    const handleChangePassword = () => {
        createOtp(userEmail)
            .then(() => {
                setOtpDialog(true);
            }).catch((e) => {
                console.log(e);
            })
    }
    const submitOtp = () => {
        setOtpDialog(false);
        setChangeDialog(true);
    }
    const onChangePasswordSubmit = (data) => {
        createPassword(userEmail, data.password1, data.password2, otp)
            .then(() => {
                setChangeDialog(false);
            }).catch((error) => {
                console.log(error);
            });
    };
    const [twoFAMode, setTwoFAMode] = useState("verify_by_email");
    const updateTwoFAMode = (mode) => {
        // props.setValue("two_step_verification", mode);
        setTwoFAMode(mode);
        updateDetails(mode);
    }
    const updateDetails = (mode) => {
        const payload = {
            two_step_verification: mode == 'verify_by_sms' ? 'sms' :
                (mode == 'verify_by_authenticator' ? 'authenticator' : 'email')
        };
        (isAdmin ? saveAdminUserSettings(userId, payload) : saveCustomerUserSettings(customerId, userId, payload))
            .then(() => userDetails())
            .catch(() => {})
    }
    return (
        <>
            <ThemeProvider theme={settingsTheme}>
                <Grid container direction='column' spacing={2} paddingBottom={6} sx={{paddingX: {xs: 0.5, md: 4}}}>
                    <Grid item xs={12}>
                        <Typography variant="h5" mt={6} mb={1} className={classes.title} style={{fontWeight: 700, fontSize: '20px', color: "#fff"}}>{user?.first_name} {user?.last_name}'s Profile</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{background: '#FFFFFF'}} orientation='horizontal' className={classes.divider}/>
                    </Grid>

                    <Grid item xs={12} style={{height: screenHeight}}>
                        <Grid container>
                            <Grid item xs={2}>
                                <CustomTabList
                                    orientation='vertical'
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    className={classes.tabs}>
                                    <CustomTab className={(activeTab === 0) ? classes.activeTab : ''} label="General" {...a11yProps(0)} />
                                    <CustomTab className={(activeTab === 1) ? classes.activeTab : ''} label="Account" {...a11yProps(1)} />
                                    {/*<CustomTab className={(activeTab === 2) ? classes.activeTab : ''} label="Sessions" {...a11yProps(2)} />*/}
                                </CustomTabList>
                            </Grid>

                            <Grid item xs={10} className={classes.tabPanel}>
                                <TabPanel value={activeTab} index={0}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFC540', mt: 4, textAlign: 'left'}} variant='h5'>Time Zone</Typography>
                                            <Divider sx={{background: '#FFFFFF', width: '25%', my: 2}}/>
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        iconStyle={{fill: 'red'}}
                                                        checked={autoTimeZone === 'True' ? true : false}
                                                        onChange={(e) => handletimeZoneAutomatic(e)}
                                                    />
                                                }
                                                label="Select Time Zone Automatically"
                                            />
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            {autoTimeZone === 'False' && (
                                                <TextField
                                                    select
                                                    label='Time Zone'
                                                    value={selectedTimeZone}
                                                    className='timezone'
                                                    onChange={handleTimeZone}
                                                >
                                                    {timeZones.map((zone, index) => (
                                                        <MenuItem key={index} value={zone.id}>
                                                            {zone.value}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFC540', mt: 4, textAlign: 'left'}} variant='h5'>Emails</Typography>
                                            <Divider sx={{background: '#FFFFFF', width: '25%', my: 2}}/>
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        // sx={{ root: { '&$checked': 'red' } }}
                                                        checked={notifyProducts === 'True' ? true : false}
                                                        onChange={handlenotifyProducts}
                                                    />
                                                }
                                                label="Notification of New Products"
                                            />
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={notifyScenarios === 'True' ? true : false}
                                                        onChange={handlenotifyScenarios}
                                                    />
                                                }
                                                label="Offers and Updates"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFC540', mt: 4, textAlign: 'left'}} variant='h5'>Notifications</Typography>
                                            <Divider sx={{background: '#FFFFFF', width: '25%', my: 2}}/>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFFFFF', textAlign: 'left'}} variant='body1'>Receive notifications as:</Typography>
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            <FormControlLabel
                                                control={
                                                    <CustomCheckbox
                                                        checked={emailNotifications === 'True' ? true : false}
                                                        onChange={handleemailNotifications}
                                                    />
                                                }
                                                label="Email"
                                            />
                                        </Grid>
                                        <Grid item xs={12} display={'flex'} alignItems={'left'}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={pushNotifications === 'True' ? true : false}
                                                        onChange={handlePushNotifications}
                                                    />
                                                }
                                                label="Push Notifications"
                                            />
                                        </Grid>
                                        {/* MFA 2 Step Verification */}
                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFC540', mt: 4, textAlign: 'left'}} variant='h5'>2 Step Verification</Typography>
                                            <Divider sx={{background: '#FFFFFF', width: '25%', my: 2}}/>
                                        </Grid>
                                        {/* <Grid item xs={12}>
                                            <Grid container direction='row' alignItems={'center'}>
                                                <Grid item sx={{
                                                    width: '200px',
                                                    textAlign: 'left'
                                                }}>
                                                    <Typography style={{ color: '#FFFFFF' }}>Verify By SMS</Typography>
                                                </Grid>
                                                <Grid item ml={2}>
                                                    <Button style={twoFAMode == 'verify_by_sms' ? {
                                                        background: '#f5b51f',
                                                        color: 'black',
                                                        border: '1px solid black'
                                                    } : {}} onClick={() => updateTwoFAMode("verify_by_sms")} variant='outlined' size='small' sx={{ textTransform: 'none', borderRadius: '30px', backgroundColor: '#3A3E3F', border: '1px solid #EEAB10', color: '#EEAB10' }}>Enable</Button>
                                                </Grid>
                                            </Grid>
                                        </Grid> */}
                                        <Grid item xs={12}>
                                            <Grid container direction='row' alignItems={'center'}>
                                                <Grid item sx={{
                                                    width: '200px',
                                                    textAlign: 'left'
                                                }}>
                                                    <Typography style={{color: '#FFFFFF'}}>Verify by Email</Typography>
                                                </Grid>
                                                <Grid item ml={2}>
                                                    <Button style={twoFAMode == 'verify_by_email' ? {
                                                        background: '#f5b51f',
                                                        color: 'black',
                                                        border: '1px solid black'
                                                    } : {}} onClick={() => updateTwoFAMode("verify_by_email")} variant='outlined' size='small' sx={{textTransform: 'none', borderRadius: '30px', backgroundColor: '#3A3E3F', border: '1px solid #EEAB10', color: '#EEAB10'}}>Enable</Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container direction='row' alignItems={'center'}>
                                                <Grid item sx={{
                                                    width: '200px',
                                                    textAlign: 'left'
                                                }}>
                                                    <Typography style={{color: '#FFFFFF'}}>Verify by Authenticator</Typography>
                                                </Grid>
                                                <Grid item ml={2}>
                                                    <Button style={twoFAMode == 'verify_by_authenticator' ? {
                                                        background: '#f5b51f',
                                                        color: 'black',
                                                        border: '1px solid black'
                                                    } : {}} onClick={() => updateTwoFAMode("verify_by_authenticator")} variant='outlined' size='small' sx={{textTransform: 'none', borderRadius: '30px', backgroundColor: '#3A3E3F', border: '1px solid #EEAB10', color: '#EEAB10'}}>Enable</Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value={activeTab} index={1}>
                                    <Grid container direction={'column'} spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography sx={{color: '#FFC540', mt: 4, textAlign: 'left'}} variant='h5'>Account</Typography>
                                            <Divider sx={{background: '#FFFFFF', width: '25%', my: 2}}/>
                                        </Grid>

                                        {/* Username */}
                                        <Grid item xs={12} display={'flex'} justifyContent={'flex-start'}>
                                            <FormControl sx={{m: 1, width: '50ch'}} variant="filled">
                                                <InputLabel>Username</InputLabel>
                                                <FilledInput
                                                    label='Username'
                                                    value={authUser?.email}
                                                    disableUnderline={true}
                                                    disabled={true}
                                                    sx={{
                                                        opacity: 0.3
                                                    }}
                                                />
                                            </FormControl>
                                        </Grid>

                                        {/* Password */}
                                        <Grid item xs={12} gap={6}>
                                            <Stack direction={'row'} gap={4} alignItems={'center'}>
                                                <FormControl sx={{m: 1, width: '50ch'}} variant="filled">
                                                    <InputLabel>Password</InputLabel>
                                                    <FilledInput
                                                        value={password}
                                                        type='password'
                                                        disableUnderline={true}
                                                        disabled={true}
                                                        sx={{
                                                            opacity: 0.3
                                                        }}
                                                    />
                                                </FormControl>
                                                <Button onClick={handleChangePassword} variant='outlined' size='small' color='primary'>Change</Button>
                                            </Stack>
                                        </Grid>
                                        <Grid item display={'flex'} justifyContent={'left'} gap={20} mt={8}>
                                            {/* Logout Button */}
                                            <Button variant='outlined' size='medium' color='primary' onClick={handleLogout}>Logout</Button>
                                            {/* Deactivate User Button */}
                                            <Button onClick={handlechangeDeactivate} variant='outlined' size='medium' color='secondary'>Deactivate</Button>
                                        </Grid>

                                    </Grid>
                                </TabPanel>
                                <TabPanel value={activeTab} index={2}>
                                    <Grid container direction={'column'} spacing={2}>
                                        {/* <Grid item xs={12}>
                                            <Typography sx={{ color: '#FFC540', mt: 4, textAlign: 'left' }} variant='h5'>Sessions</Typography>
                                            <Divider sx={{ background: '#FFFFFF', width: '25%', my: 2 }} />
                                        </Grid> */}
                                        <Grid item xs={12}>
                                            <Sessions></Sessions>
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Dialog open={otpDialog} fullWidth={true} PaperProps={{sx: {borderRadius: "12px", backgroundColor: '#3A3E3F'}}}>
                    <DialogContent>
                        <Grid container direction='column' alignContent='center' justifyContent='center' spacing={1} sx={{
                            color: '#FFF',
                            fontFamily: ' "Poppins", sans-serif !important',
                            fontSize: '24px',
                            fontWeight: '700',
                            marginTop: '14px'
                        }}>
                            <Grid item>
                                <Typography component="h1" variant="h5" sx={{
                                    fontFamily: ' "Poppins", sans-serif !important',
                                    fontWeight: '600px',
                                    fontSize: '24px'
                                }}>
                                    Enter OTP
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography style={{fontSize: "14px", color: "#C7CCD0", fontWeight: "400"}}>An OTP has been sent to your email address. </Typography>
                            </Grid>
                            <Grid item>
                                <OtpInput alignItems="start"
                                          value={otp}
                                          onChange={setOtp}
                                          numInputs={6}
                                          renderSeparator={<span style={{
                                              fontSize: "22px",
                                              marginLeft: "5px",
                                              marginRight: "5px",
                                          }}></span>}
                                          renderInput={(props) => <input {...props} type="text"/>}
                                          inputType={'text'}
                                          inputStyle={{
                                              width: "60px",
                                              marginBottom: "10px",
                                              height: "70px",
                                              borderRadius: "8px",
                                              border: "1px solid var(--Gray-1, #C7CCD0)",
                                              background: "var(--Charcoal, #3A3E3F)",
                                              outline: "none",
                                              color: "#fff",
                                              fontSize: "22px",
                                              fontWeight: "400"

                                          }}
                                />
                            </Grid>
                            <Grid item>
                                <Typography style={{textAlign: "end", width: "100%", marginTop: "12px", marginBottom: "12px"}}>
                                    {/* <a href="#" style={{ color: "#C7CCD0", fontSize: "14px", fontWeight: "24px" }} onClick={resendOtp}>Resend OTP</a> or <a href="#" style={{ color: "#C7CCD0", fontSize: "14px", fontWeight: "24px" }} onClick={changeMail}>Change Email</a> */}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Grid container direction='row' justifyContent='flex-end' spacing={2}>
                                    <Grid item>
                                        <Button variant='text' sx={{color: '#FFC540'}} onClick={() => setOtpDialog(false)}>Cancel</Button>
                                    </Grid>
                                    <Grid item>
                                        <StyledButtonContained variant="contained"
                                                               onClick={submitOtp}
                                                               type="button"
                                                               sx={{
                                                                   width: '140px',
                                                                   height: '50px'
                                                               }}>
                                            Submit
                                        </StyledButtonContained>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>

                </Dialog>

            </ThemeProvider>
            <Dialog open={changeDailog} fullWidth={true} PaperProps={{sx: {borderRadius: "12px", backgroundColor: '#3A3E3F'}}}>
                <DialogContent>
                    <Box p={3}>
                        <form onSubmit={handleSubmit(onChangePasswordSubmit)}>
                            {/* <Item> */}
                            <Grid container direction='column' spacing={1} xs={{
                                color: '#FFF',
                                fontFamily: ' "Poppins", sans-serif !important',
                                fontSize: '24px',
                                fontWeight: '700'
                            }}>
                                <Grid item>
                                    <Typography align='center' sx={{
                                        fontFamily: ' "Poppins", sans-serif !important',
                                        fontWeight: '600px',
                                        fontSize: '30px',
                                        color: '#FFFFFF'
                                    }}>
                                        Create New Password
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={-2}>
                                        <Typography align='left' sx={{
                                            fontSize: '14px',
                                            color: '#C7CCD0',
                                            fontWeight: 400
                                        }}>Enter password</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth={true}>
                                        <TextField
                                            fullWidth={true}
                                            margin="normal"
                                            required
                                            id="username"
                                            type={showPassword1 ? 'text' : 'password'}
                                            label="Enter Password"
                                            name="password1"
                                            {...register("password1", {required: true})}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderRadius: '8px',
                                                        borderColor: '#C7CCD0',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#C7CCD0',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#C7CCD0',
                                                    }
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    color: '#FFFFFF',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.5)',
                                                    '&.Mui-focused': {
                                                        color: '#FFC540'
                                                    },
                                                },
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword1}
                                                        onMouseDown={handleMouseDownPassword1}
                                                        edge="end"
                                                        sx={{color: '#FFFFFF'}}
                                                    >
                                                        {showPassword1 ? <VisibilityOff/> : <Visibility/>}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                    <Box mb={-2}>
                                        <Typography align='left' sx={{
                                            fontSize: '14px',
                                            color: '#C7CCD0',
                                            fontWeight: 400
                                        }}>Repeat password</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth={true}>
                                        <TextField
                                            fullWidth={true}
                                            margin="normal"
                                            required
                                            name="RepeatPassword"
                                            label="Repeat Password"
                                            type={showPassword2 ? 'text' : 'password'}
                                            id="password"
                                            {...register("password2", {
                                                required: true,
                                                // pattern: "",
                                                validate: (val) => {
                                                    if (watch('password1') != val) {
                                                        return "Your passwords do not match";
                                                    } else if (!passwordRegex.test(val)) {
                                                        return "Please set a password with minimum length of 8 characters, containing at least 1 uppercase letter, 1 lowercase letter, and 1 digit."
                                                    }
                                                }
                                            })}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderRadius: '8px',
                                                        borderColor: '#C7CCD0',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#C7CCD0',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#C7CCD0',
                                                    }
                                                },
                                                '& .MuiOutlinedInput-input': {

                                                    color: '#FFFFFF',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.5)',
                                                    '&.Mui-focused': {
                                                        color: '#FFC540'
                                                    },
                                                },
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword2}
                                                        onMouseDown={handleMouseDownPassword2}
                                                        edge="end"
                                                        sx={{color: '#FFFFFF'}}
                                                    >
                                                        {showPassword2 ? <VisibilityOff/> : <Visibility/>}
                                                    </IconButton>
                                                </InputAdornment>
                                            }

                                        />
                                        {errors.password2 && <p style={{
                                            fontSize: '12px',
                                            color: '#eb4b10',
                                            width: '100%',
                                            textAlign: 'left',
                                            marginTop: '-2px'
                                        }}>{errors.password2?.message}</p>}
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <Grid container direction='row' justifyContent='flex-end' spacing={1}>
                                        <Grid item>
                                            <StyledButtonOutlined variant="contained"
                                                                  onClick={() => setChangeDialog(false)}
                                                                  type="button"
                                                                  sx={{
                                                                      width: '140px',
                                                                      height: '50px',
                                                                  }}>
                                                Cancel
                                            </StyledButtonOutlined>
                                        </Grid>
                                        <Grid item>
                                            <StyledButtonContained variant="contained"
                                                                   type="submit"
                                                                   sx={{
                                                                       width: '140px',
                                                                       height: '50px'
                                                                   }}>
                                                Submit
                                            </StyledButtonContained>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                </DialogContent>
            </Dialog>
        </>

    );
};

const CustomCheckbox = styled(Checkbox)(({theme}) => ({
    // color: theme.status.danger,
    '&.Mui-checked': {
        color: 'red',
    },
}));


//import { getApiUrl } from "./../../global";